import React from "react";
import { Box, Button, makeStyles, MenuItem, TextField } from "@material-ui/core";
import CounterfeitTable from "../../table/counterfeitTable";
import { Formik } from "formik";
import DateRangePicker from "components/input/dateRangePicker";
import { getLang } from "app/feature/constants";
import clsx from "clsx";

const useStyle = makeStyles((theme) => ({
  searchContainer: {
    backgroundColor: theme.palette.background.paper,
    display: "grid",
    gridTemplateColumns: "4fr 2fr 2fr 1fr 1.5fr",
    columnGap: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      gridRowGap: theme.spacing(1),
      rowGap: theme.spacing(1),
    },
  },
  inputRootOrder: {
    paddingTop: "8px !important",
    paddingBottom: "7px !important",
    paddingLeft: "12px !important",
    paddingRight: "12px !important",
    fontSize: "0.875rem",
    fontWeight: 500
  },
  emptyField: {
    "& .MuiInputBase-root": {
      color: "#A4A6A8",
    },
  },
  searchWithDropdown: {
    display: "flex",
    backgroundColor: "#eceff0",
    borderRadius: "4px",
    overflow: "hidden",
    alignItems: "center",
  },
  searchTypeSelect: {
    minWidth: "130px",
    "& .MuiFilledInput-root": {
      backgroundColor: "transparent",
      borderRadius: "0",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiFilledInput-input": {
      paddingTop: "10px",
      paddingBottom: "10px",
      paddingRight: "24px",
      fontSize: "0.875rem",
      fontWeight: 500,
    },
    "& .MuiFilledInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiFilledInput-underline:after": {
      borderBottom: "none",
    },
    "& .MuiSelect-icon": {
      right: "4px",
    },
  },
  searchDivider: {
    width: "1px",
    height: "24px",
    backgroundColor: "#ccc",
  },
  searchInput: {
    flex: 1,
    "& .MuiFilledInput-root": {
      backgroundColor: "transparent",
      borderRadius: "0",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    "& .MuiFilledInput-input": {
      paddingTop: "10px",
      paddingBottom: "10px",
      paddingLeft: "12px",
    },
    "& .MuiFilledInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiFilledInput-underline:after": {
      borderBottom: "none",
    },
  },
  statusSelect: {
    backgroundColor: "#eceff0",
    borderRadius: "4px",
    "& .MuiFilledInput-root": {
      backgroundColor: "#eceff0",
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: "#eceff0",
      },
      "&.Mui-focused": {
        backgroundColor: "#eceff0",
      },
    },
    "& .MuiFilledInput-input": {
      paddingTop: "10px",
      paddingBottom: "10px",
      fontSize: "0.875rem",
      fontWeight: 500,
    },
    "& .MuiFilledInput-underline:before": {
      borderBottom: "none",
    },
    "& .MuiFilledInput-underline:after": {
      borderBottom: "none",
    },
  },
  datePickerContainer: {
    "& .rs-picker": {
      width: "100%",
    },
    "& .rs-picker-toggle": {
      backgroundColor: "#eceff0 !important",
      borderRadius: "4px !important",
      border: "none !important",
      paddingTop: "9px !important",
      paddingBottom: "9px !important",
      paddingLeft: "12px !important",
      paddingRight: "32px !important",
      fontSize: "0.875rem !important",
      fontWeight: "500 !important",
    },
    "& .rs-picker-default .rs-picker-toggle": {
      backgroundColor: "#eceff0 !important",
    },
    "& .rs-picker-default .rs-picker-toggle.rs-btn": {
      backgroundColor: "#eceff0 !important",
    },
    "& .rs-picker-toggle-wrapper": {
      width: "100%",
    },
    "& .rs-picker-toggle-caret": {
      top: "50% !important",
      transform: "translateY(-50%) !important",
    },
    "& .rs-picker-toggle-placeholder": {
      fontSize: "0.875rem !important",
    },
  },
}));

export default function CounterfeitPanelComponent({
  isFetching,
  counterfeitReportData,
  handleChangePage,
  handleChangeRowsPerPage,
  handleSearch,
  isLoading,
  totalFiltered,
  handleReload,
  paginationOptions,
  lang,
  tableFilter,
  clearSearch,
  changeOrder
}) {
  const classes = useStyle();

  return (
    <Box pt={2}>
      <Formik
        initialValues={{
          search: tableFilter.search,
          searchType: tableFilter.searchType || "serial_number",
          status: tableFilter.status || "all",
          dateRange:
            tableFilter.fromDate && tableFilter.toDate
              ? {
                  from: tableFilter.fromDate,
                  to: tableFilter.toDate,
                }
              : null,
        }}
        enableReinitialize
        onSubmit={handleSearch}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <Box p={2} mb={2} className={classes.searchContainer}>
              {/* Combined Search Type Dropdown + Search Input */}
              <Box className={classes.searchWithDropdown}>
                <TextField
                  select
                  hiddenLabel
                  variant="filled"
                  size="small"
                  margin="none"
                  className={classes.searchTypeSelect}
                  SelectProps={{
                    MenuProps: {
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    },
                  }}
                  {...formik.getFieldProps("searchType")}
                >
                  <MenuItem value="serial_number">{getLang(lang, "label.SERIAL_NUMBER")}</MenuItem>
                  <MenuItem value="product_name">{getLang(lang, "label.PRODUCT_NAME")}</MenuItem>
                  <MenuItem value="consumer_name">{getLang(lang, "label.CONSUMER_NAME")}</MenuItem>
                  <MenuItem value="contact">{getLang(lang, "label.CONTACT_NUMBER")}</MenuItem>
                  <MenuItem value="email">{getLang(lang, "label.EMAIL")}</MenuItem>
                </TextField>
                <Box className={classes.searchDivider} />
                <TextField
                  hiddenLabel
                  variant="filled"
                  size="small"
                  placeholder={getLang(lang, "placeholder.SEARCH")}
                  margin="none"
                  className={classes.searchInput}
                  InputProps={{
                    disableUnderline: true,
                  }}
                  {...formik.getFieldProps("search")}
                />
              </Box>
              {/* Status Dropdown */}
              <TextField
                select
                hiddenLabel
                variant="filled"
                size="small"
                margin="none"
                className={classes.statusSelect}
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                  },
                }}
                {...formik.getFieldProps("status")}
              >
                <MenuItem value="all">{getLang(lang, "label.ALL_STATUS")}</MenuItem>
                <MenuItem value="pending">{getLang(lang, "label.PENDING")}</MenuItem>
                <MenuItem value="processed">{getLang(lang, "label.PROCESSED")}</MenuItem>
              </TextField>
              {/* Date Range Picker */}
              <Box className={classes.datePickerContainer}>
                <DateRangePicker
                  label=""
                  placeholder={getLang(lang, "placeholder.REPORTED_DATE")}
                  disabled={isFetching}
                  disableFuture={true}
                  className={clsx({
                    [classes.emptyField]:
                      !formik.getFieldProps("dateRange").value,
                  })}
                  id={"created-picker-dialog"}
                  value={formik.getFieldProps("dateRange").value}
                  handleChange={(v) => {
                    formik.setFieldValue("dateRange", v);
                  }}
                  InputPropStyle={{
                    color: !formik.getFieldProps("dateRange").value
                      ? "#A4A6A8"
                      : "#000000",
                    fontWeight: "normal",
                  }}
                  inputProps={{
                    fontWeight: "400",
                    fontSize: "0.875rem",
                  }}
                  title={getLang(lang, "placeholder.REPORTED_DATE")}
                />
              </Box>
              <Button
                variant="text"
                disableElevation
                color="secondary"
                size="small"
                disabled={isFetching}
                onClick={() => {
                  clearSearch();
                }}
              >
                {getLang(lang, "label.CLEAR")}
              </Button>
              <Button
                disabled={isFetching}
                variant="contained"
                disableElevation
                color="primary"
                size="small"
                type="submit"
              >
                {getLang(lang, "label.SEARCH")}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
      <CounterfeitTable
        isFetching={isFetching}
        counterfeitReportData={counterfeitReportData}
        rowsPerPage={tableFilter.rowsPerPage}
        page={tableFilter.page}
        search={tableFilter.search}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        isLoading={isLoading}
        totalFiltered={totalFiltered}
        handleReload={handleReload}
        paginationOptions={paginationOptions}
        lang={lang}
        tableFilter={tableFilter}
        changeOrder={changeOrder}
      />
    </Box>
  );
}
